import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";
import { buildParams, PAGINATION_SEARCH_FIELDS } from "../http-params.util";

/**
 * SfdcApiService (Angular 18) — Salesforce integration endpoints.
 *
 * Replaces apps/b2b-ui/common/services/api/sfdcApi/sfdcApi.service.js.
 *
 * `pushData(props)` URL pattern preserved 1:1: when `props.objectType` is
 * `'orders'` the path includes a `visitation/` segment between the base and
 * the object type. The `tenantId` querystring param is forwarded when
 * present.
 */
const INTEGRATION_BASE_V2 = "/v2/api/integration";
const SFDC_ORDER_BASE_V2 = `${INTEGRATION_BASE_V2}/sfdc/search/order`;
const SFDC_MEMBERSHIP_BASE_V2 = `${INTEGRATION_BASE_V2}/sfdc/search/membership`;
const SFDC_CUSTOMER_BASE_V2 = `${INTEGRATION_BASE_V2}/sfdc/search/customer`;
const SFDC_LOGS_BASE_V2 = `${INTEGRATION_BASE_V2}/request-logs`;

const SFDC_LOGS_LIST_FIELDS = [
  "operation",
  "target",
  "objectId",
  ...PAGINATION_SEARCH_FIELDS,
] as const;

interface PushDataProps {
  acmeId: number | string;
  objectType: string;
  tenantId?: string;
}

@Injectable({ providedIn: "root" })
export class SfdcApiService {
  private readonly http = inject(HttpClient);

  pushData(props: PushDataProps): Promise<AjsResponse<unknown>> {
    const typeSegment =
      (props.objectType === "orders" ? "visitation/" : "") + props.objectType;
    return wrapAjs(
      this.http.post<unknown>(
        `${INTEGRATION_BASE_V2}/${typeSegment}/${props.acmeId}`,
        null,
        { params: buildParams({ tenantId: props.tenantId }) },
      ),
    );
  }

  readonly order = {
    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${SFDC_ORDER_BASE_V2}/${id}`)),
  };

  readonly membership = {
    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${SFDC_MEMBERSHIP_BASE_V2}/${id}`)),
  };

  readonly customer = {
    detail: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${SFDC_CUSTOMER_BASE_V2}/${id}`)),
  };

  readonly logs = {
    get: (id: number | string): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.get<unknown>(`${SFDC_LOGS_BASE_V2}/${id}`)),

    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${SFDC_LOGS_BASE_V2}/search`, {
          params: buildParams(props, SFDC_LOGS_LIST_FIELDS),
        }),
      ),
  };
}
